import { History, Location } from 'history';

import { IFilterDefinition, OptionType } from '@evinced-private/ui-common';

import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { Logger } from 'src/common/services/Logger';

export const filtersToQueryParams = (filters: IFilterOptions): URLSearchParams => {
	const params = new URLSearchParams();
	Object.entries(filters).forEach(([key, value]) => {
		if (typeof value === 'string' && value.trim()) {
			params.set(key, value.trim());
		} else if (Array.isArray(value) && value.length > 0) {
			const serialized = value
				.map((opt) => encodeURIComponent(String(opt.value ?? '')))
				.filter(Boolean)
				.join(',');
			if (serialized) {
				params.set(key, serialized);
			}
		}
	});
	return params;
};

export const queryParamsToFilters = (
	searchParams: URLSearchParams,
	filterDefinitions: IFilterDefinition[],
	filterOptions: Record<string, OptionType[]>
): IFilterOptions => {
	const filters: IFilterOptions = {};
	const defMap = new Map(filterDefinitions.map((def) => [def.id, def]));

	searchParams.forEach((value, key) => {
		const def = defMap.get(key);
		if (!def) {
			return;
		}

		if (def.filterType === 'text') {
			const trimmed = value.trim();
			if (trimmed) {
				filters[key] = trimmed;
			}
			return;
		}

		const options = filterOptions[key];
		if (!options?.length) {
			Logger.warn(`Query param "${key}" skipped — options not yet loaded.`);
			return;
		}

		const rawValues = value
			.split(',')
			.map((v) => decodeURIComponent(v.trim()))
			.filter(Boolean);

		const findOption = (v: string): OptionType | undefined =>
			options.find((o) => String(o.value).trim() === v || String(o.label || '').trim() === v);

		const matched = rawValues.flatMap((v) => {
			const opt = findOption(v);
			return opt ? [opt] : [];
		});
		const unmatched = rawValues.filter((v) => !findOption(v));

		if (unmatched.length) {
			Logger.warn(
				`URL values for "${key}" could not be matched to available options: ${unmatched.join(', ')}`
			);
		}

		if (matched.length) {
			filters[key] = matched;
		}
	});

	return filters;
};

export const updateUrlWithFilters = (
	history: History,
	location: Location,
	filters: IFilterOptions,
	filterDefinitions: IFilterDefinition[]
): void => {
	const params = filtersToQueryParams(filters);
	const filterKeys = new Set(filterDefinitions.map((def) => def.id));
	new URLSearchParams(location.search).forEach((value, key) => {
		if (!filterKeys.has(key)) {
			params.set(key, value);
		}
	});
	const qs = params.toString();
	const newSearch = qs ? `?${qs}` : '';
	if (location?.search !== newSearch) {
		history.replace({ pathname: location.pathname, search: newSearch, state: location.state });
	}
};
